import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import axios from 'axios';
import { toast } from "react-toastify";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilePen, 
  faArrowLeft, 
  faUtensils, 
  faTrash, 
  faPlus, 
  faSave,
  faReceipt,
  faCalculator,
  faTag,
  faCircleExclamation,
  faUserCircle,
  faEnvelope,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../../config/api.config';

const EditInvoice = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  
  const [state, setState] = useState({
    invoice: {
      foods: [],
      subtotal: 0,
      cgst: 0,
      sgst: 0,
      roundOffAmount: 0,
      finalAmount: 0,
      invoiceNumber: '',
      invoiceDate: '',
      discount: 0,
      reservedTableInfo: null,
      userId: null
    },
    loading: true,
    error: null,
    foodsList: [],
    selectedFood: '',
  });

  const fetchInvoiceDetail = async () => {
    try {
      const uDetails = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = uDetails?.role === 'owner' ? selectedRestId : uDetails?.restaurantId;

      // Handle populated restaurantId object
      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      if (!activeRestaurantId) {
        setState(prev => ({ ...prev, error: 'No restaurant context found', loading: false }));
        return;
      }

      const response = await axios.get(buildApiUrl(`/invoice/admin/${invoiceId}?restaurantId=${activeRestaurantId}`), {
        headers: { 'auth-token': localStorage.getItem('token') }
      });
      const invoiceData = response.data;
      
      const normalizedFoods = (invoiceData.foods || []).map(f => ({
        ...f,
        foodId: f.foodId?._id || f.foodId || '', 
        total: f.total || (f.price * f.quantity) || 0
      }));

      const subtotal = normalizedFoods.reduce((sum, food) => sum + (food.total || 0), 0);
      const cgst = subtotal * 0.025;
      const sgst = subtotal * 0.025;
      const totalWithGst = subtotal + cgst + sgst;
      
      const fetchedDiscountAmount = Number((invoiceData.discount || 0).toFixed(2));
      let initialDiscountPercent = 0;
      if (totalWithGst > 0 && fetchedDiscountAmount > 0) {
          initialDiscountPercent = Number(((fetchedDiscountAmount / totalWithGst) * 100).toFixed(2));
      }

      const calculated = calculateTotalAmount(
        normalizedFoods,
        initialDiscountPercent,
        invoiceData.reservedTableInfo
      );

      setState(prev => ({
        ...prev,
        invoice: {
          ...invoiceData,
          foods: normalizedFoods,
          ...calculated,
          invoiceNumber: invoiceData.invoiceNumber || '',
          invoiceDate: invoiceData.invoiceDate || '',
          discount: initialDiscountPercent,
          discountAmount: calculated.discountAmount,
          reservedTableInfo: invoiceData.reservedTableInfo || null,
        },
        loading: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Error fetching invoice details',
        loading: false
      }));
      console.error('Error fetching invoice details:', error);
    }
  };

  const fetchFoods = async () => {
    try {
      const uDetails = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = uDetails?.role === 'owner' ? selectedRestId : uDetails?.restaurantId;

      // Handle populated restaurantId object
      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      if (!activeRestaurantId) {
        setState(prev => ({ ...prev, foodsList: [] }));
        return;
      }

      const response = await axios.get(buildApiUrl(`/food/list?restaurantId=${activeRestaurantId}`));
      setState(prev => ({
        ...prev,
        foodsList: response.data.data || []
      }));
    } catch (error) {
      console.error('Error fetching food items:', error);
    }
  };

  const calculateTotalAmount = (foods, discountPercent = 0, reservedTableInfo = null) => {
    const subtotal = foods.reduce((sum, food) => sum + (food.total || 0), 0);
    
    // 1. Calculate GST on the raw item subtotal (pre-discount)
    const cgst = parseFloat((subtotal * 0.025).toFixed(2));
    const sgst = parseFloat((subtotal * 0.025).toFixed(2));
    const totalWithGst = subtotal + cgst + sgst;
    
    // 2. Final calculations (deducting the discount from the GST-included total)
    const discountAmount = totalWithGst * (discountPercent / 100);
    const totalBeforeRoundOff = totalWithGst - discountAmount;
    
    const hasValidReservation = reservedTableInfo && (
      (Array.isArray(reservedTableInfo) && reservedTableInfo.length > 0) || 
      (!Array.isArray(reservedTableInfo) && (reservedTableInfo.status === 'succeeded' || reservedTableInfo.tableNumber))
    );
    const tableReservationDiscount = hasValidReservation ? 100 : 0;
    const totalAfterReservationDiscount = Math.max(0, totalBeforeRoundOff - tableReservationDiscount);
    
    const roundOffAmount = Math.round(totalAfterReservationDiscount) - totalAfterReservationDiscount;
    const finalAmount = (totalAfterReservationDiscount + roundOffAmount).toFixed(2);

    return { 
      subtotal, 
      cgst, 
      sgst, 
      roundOffAmount, 
      finalAmount,
      tableReservationDiscount
    };
  };

  const handleFoodChange = (index, e) => {
    const { name, value } = e.target;
    const numericValue = parseFloat(value) || 0;
    
    setState(prev => {
      const updatedFoods = [...prev.invoice.foods];
      const updatedFood = { ...updatedFoods[index] };
      
      if (name === 'quantity') {
        updatedFood.quantity = Math.max(1, numericValue);
        updatedFood.total = (updatedFood.price || 0) * updatedFood.quantity;
      } else if (name === 'price') {
        updatedFood.price = Math.max(0, numericValue);
        updatedFood.total = updatedFood.price * (updatedFood.quantity || 1);
      }
      
      updatedFoods[index] = updatedFood;
      const calculated = calculateTotalAmount(
        updatedFoods, 
        prev.invoice.discount,
        prev.invoice.reservedTableInfo
      );
      
      return {
        ...prev,
        invoice: {
          ...prev.invoice,
          foods: updatedFoods,
          ...calculated
        }
      };
    });
  };

  const handleAddFoodItem = (foodId) => {
    if (!foodId) return;
    
    setState(prev => {
      const selectedFoodItem = prev.foodsList.find(food => food._id === foodId);
      if (!selectedFoodItem) return prev;
      
      const isAlreadyAdded = prev.invoice.foods.some(food => food.foodId === foodId);
      if (isAlreadyAdded) {
        toast.warning("Item already present in list");
        return { ...prev, selectedFood: '' };
      }

      const newFoodItem = {
        foodId: selectedFoodItem._id,
        name: selectedFoodItem.name || '',
        price: selectedFoodItem.price || 0,
        quantity: 1,
        total: selectedFoodItem.price || 0,
      };

      const updatedFoods = [...prev.invoice.foods, newFoodItem];
      const calculated = calculateTotalAmount(
        updatedFoods, 
        prev.invoice.discount,
        prev.invoice.reservedTableInfo
      );
      
      return {
        ...prev,
        invoice: {
          ...prev.invoice,
          foods: updatedFoods,
          ...calculated
        },
        selectedFood: ''
      };
    });
  };

  const handleRemoveFoodItem = (index) => {
    setState(prev => {
      const updatedFoods = [...prev.invoice.foods];
      updatedFoods.splice(index, 1);
      const calculated = calculateTotalAmount(
        updatedFoods, 
        prev.invoice.discount,
        prev.invoice.reservedTableInfo
      );
      
      return {
        ...prev,
        invoice: {
          ...prev.invoice,
          foods: updatedFoods,
          ...calculated
        }
      };
    });
  };

  const handleDiscountChange = (e) => {
    const discountPercent = Math.max(0, parseFloat(e.target.value) || 0);
    
    setState(prev => {
      const calculated = calculateTotalAmount(
        prev.invoice.foods, 
        discountPercent,
        prev.invoice.reservedTableInfo
      );
      return {
        ...prev,
        invoice: {
          ...prev.invoice,
          discount: discountPercent,
          ...calculated
        }
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (state.invoice.foods.length === 0) {
      toast.error("Invoice must contain at least one food item");
      return;
    }

    if (state.invoice.status === 'cancelled') {
        toast.error("Cannot modify a cancelled/voided invoice");
        return;
    }

    const invalidFood = state.invoice.foods.find(f => !f.foodId);
    if (invalidFood) {
      toast.error(`FoodId missing for item: ${invalidFood.name || 'Unknown'}`);
      return;
    }

    const subtotal = state.invoice.foods.reduce((sum, food) => sum + (food.total || 0), 0);
    const cgst = parseFloat((subtotal * 0.025).toFixed(2));
    const sgst = parseFloat((subtotal * 0.025).toFixed(2));
    const totalWithGst = subtotal + cgst + sgst;
    const discountAmount = totalWithGst * ((state.invoice.discount || 0) / 100);
    const totalBeforeRoundOff = totalWithGst - discountAmount;
    const rti = state.invoice.reservedTableInfo;
    const isReservationValid = rti && (
      (Array.isArray(rti) && rti.length > 0 && rti.some(info => info.status === 'succeeded' || info.tableNumber)) || 
      (!Array.isArray(rti) && (rti.status === 'succeeded' || rti.tableNumber))
    );
    const tableReservationDiscount = isReservationValid ? 100 : 0;
    const totalAfterReservationDiscount = Math.max(0, totalBeforeRoundOff - tableReservationDiscount);
    const roundOffAmount = Math.round(totalAfterReservationDiscount) - totalAfterReservationDiscount;
    const finalAmount = (totalAfterReservationDiscount + roundOffAmount).toFixed(2);
  
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

    // Handle populated restaurantId object
    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    const invoiceData = {
      foods: state.invoice.foods.map(food => ({
        foodId: food.foodId,
        name: food.name,
        price: parseFloat(food.price) || 0,
        quantity: parseInt(food.quantity) || 1,
        total: parseFloat(food.total) || 0,
      })),
      discount: parseFloat(discountAmount.toFixed(2)),
      subtotal: parseFloat(subtotal) || 0,
      cgst: parseFloat(cgst).toFixed(2) || 0,
      sgst: parseFloat(sgst).toFixed(2) || 0,
      roundOff: parseFloat(roundOffAmount).toFixed(2) || 0,
      finalAmount: parseFloat(finalAmount) || 0,
      totalAmount: parseFloat(subtotal) || 0,
      reservedTableInfo: state.invoice.reservedTableInfo || null,
      restaurantId: activeRestaurantId
    };
  
    try {
      const response = await axios.put(
        buildApiUrl(`/invoice/admin/update/${invoiceId}`),
        invoiceData,
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
  
      toast.success("Invoice documentation updated");
      navigate("/admin/all-invoices");
    } catch (error) {
      toast.error(error.response?.data?.message || "Modification failed to post");
    }
  };

  useEffect(() => {
    fetchInvoiceDetail();
    fetchFoods();
  }, [invoiceId]);

  useEffect(() => {
    if (state.selectedFood) {
      handleAddFoodItem(state.selectedFood);
    }
  }, [state.selectedFood]);

  if (state.loading) {
    return (
      <div className="admin-layout">
        <Sidebar />
        <main>
           <div className="tf-flex-center" style={{ padding: '10rem 2rem', flexDirection: 'column', gap: '1.5rem' }}>
              <div className="tf-spinner"></div>
              <p style={{ color: 'var(--tf-text-muted)', fontWeight: '500' }}>Synchronizing invoice detail...</p>
           </div>
        </main>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="admin-layout">
        <Sidebar />
        <main>
           <div className="tf-empty-state">
              <FontAwesomeIcon icon={faCircleExclamation} size="3x" style={{ color: 'var(--tf-error)', marginBottom: '1.5rem', opacity: 0.1 }} />
              <p>{state.error}</p>
              <button className="tf-btn tf-btn-primary" style={{ marginTop: '1rem' }} onClick={() => navigate(-1)}>Back to Registry</button>
           </div>
        </main>
      </div>
    );
  }

  const user = state.invoice.userId;

  return (
    <div className="admin-layout">
      <Sidebar />
      <main>
        <div className="tf-card" style={{ marginBottom: '2rem' }}>
          <header style={{ marginBottom: '3rem' }}>
            <button 
              onClick={() => navigate(-1)} 
              className="tf-btn" 
              style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--tf-text-muted)', padding: '0.5rem 1rem', fontSize: '0.85rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Registry
            </button>
            
            <div className="tf-flex" style={{ gap: '1rem', alignItems: 'center' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: 'linear-gradient(135deg, var(--tf-primary) 0%, #ff6b6b 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem' }}>
                <FontAwesomeIcon icon={faFilePen} />
              </div>
              <div>
                <h1 className="tf-heading-md" style={{ margin: 0, fontSize: '1.8rem' }}>Modify Invoice #{state.invoice.invoiceNumber}</h1>
                <p style={{ color: 'var(--tf-text-muted)', margin: 0 }}>Adjustment interface for existing transaction records</p>
              </div>
            </div>
          </header>

          <form onSubmit={handleSubmit} className="tf-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: '2.5rem', alignItems: 'flex-start' }}>
            <div className="tf-grid" style={{ gap: '2.5rem' }}>
              
              {/* Customer Identity Card */}
              <div className="tf-card" style={{ padding: '1.5rem' }}>
                 <h3 className="tf-text-accent" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', fontSize: '1rem' }}>
                    <FontAwesomeIcon icon={faUserCircle} />
                    Customer Identity
                 </h3>
                 <div className="tf-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                    <div className="tf-flex" style={{ gap: '1rem' }}>
                       <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'var(--tf-bg-input)', color: 'var(--tf-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', border: '1px solid var(--tf-border)' }}>
                          <FontAwesomeIcon icon={faUserCircle} />
                       </div>
                       <div>
                          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--tf-text-muted)', fontWeight: '700' }}>Account Holder</div>
                          <div style={{ fontWeight: '600' }}>{user?.name || 'Walk-in Customer'}</div>
                       </div>
                    </div>
                    <div className="tf-flex" style={{ gap: '1rem' }}>
                       <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'var(--tf-bg-input)', color: 'var(--tf-text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', border: '1px solid var(--tf-border)' }}>
                          <FontAwesomeIcon icon={faEnvelope} />
                       </div>
                       <div>
                          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--tf-text-muted)', fontWeight: '700' }}>Email Address</div>
                          <div style={{ fontWeight: '600' }}>{user?.email || 'N/A'}</div>
                       </div>
                    </div>
                    <div className="tf-flex" style={{ gap: '1rem' }}>
                       <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'var(--tf-bg-input)', color: 'var(--tf-text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', border: '1px solid var(--tf-border)' }}>
                          <FontAwesomeIcon icon={faPhone} />
                       </div>
                       <div>
                          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--tf-text-muted)', fontWeight: '700' }}>Contact</div>
                          <div style={{ fontWeight: '600' }}>{user?.contact || 'N/A'}</div>
                       </div>
                    </div>
                 </div>
              </div>

              {/* Items Section */}
              <div className="tf-card" style={{ padding: '1.5rem' }}>
                <h3 className="tf-text-accent" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', fontSize: '1rem' }}>
                  <FontAwesomeIcon icon={faUtensils} />
                  Journalized Food Items
                </h3>
                
                <div className="tf-list-container">
                  <div className="tf-list-header" style={{ gridTemplateColumns: '40px 1.5fr 1fr 100px 120px 60px', gap: '1rem', padding: '1rem 1.5rem' }}>
                    <span>#</span>
                    <span>MENU ITEM</span>
                    <span style={{ textAlign: 'right' }}>PRICE</span>
                    <span style={{ textAlign: 'center' }}>QTY</span>
                    <span style={{ textAlign: 'right' }}>TOTAL</span>
                    <span style={{ textAlign: 'right' }}></span>
                  </div>
                  
                  <div className="tf-grid" style={{ gap: '0' }}>
                    {state.invoice.foods.map((food, index) => (
                      <div key={`${food.foodId}-${index}`} className="tf-list-item" style={{ gridTemplateColumns: '40px 1.5fr 1fr 100px 120px 60px', gap: '1rem', padding: '1rem 1.5rem' }}>
                        <span style={{ color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>{index + 1}</span>
                        <span style={{ fontWeight: '600' }}>{food.name}</span>
                        <span style={{ textAlign: 'right', color: 'var(--tf-text-muted)' }}>₹{food.price.toFixed(2)}</span>
                        <div style={{ textAlign: 'center' }}>
                          <input
                            type="number"
                            name="quantity"
                            value={food.quantity}
                            onChange={(e) => handleFoodChange(index, e)}
                            min="1"
                            className="tf-input"
                            style={{ padding: '0.3rem 0.5rem', textAlign: 'center', width: '60px', fontSize: '0.85rem' }}
                          />
                        </div>
                        <span style={{ textAlign: 'right', fontWeight: '700', color: 'var(--tf-primary)' }}>₹{food.total.toFixed(2)}</span>
                        <div style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={() => handleRemoveFoodItem(index)}
                            className="tf-btn"
                            style={{ padding: 0, width: '32px', height: '32px', background: 'rgba(255, 56, 96, 0.1)', color: 'var(--tf-error)' }}
                          >
                             <FontAwesomeIcon icon={faTrash} size="xs" />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {state.invoice.foods.length === 0 && (
                      <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--tf-text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                        No items current journalized. Please select from the dropdown below.
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px dashed var(--tf-border)' }}>
                   <div className="tf-flex" style={{ gap: '1.5rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '250px' }}>
                        <p className="tf-text-accent" style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>Annex New Item</p>
                        <select
                          className="tf-input"
                          value={state.selectedFood}
                          onChange={(e) => setState(prev => ({...prev, selectedFood: e.target.value}))}
                        >
                          <option value="">Search menu records...</option>
                          {state.foodsList.map((food) => (
                            <option key={food._id} value={food._id}>
                              {food.name} (₹{food.price.toFixed(2)})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="tf-flex" style={{ alignItems: 'flex-end' }}>
                         <button type="button" className="tf-btn tf-btn-primary" style={{ padding: '0.85rem 2rem' }}>
                            <FontAwesomeIcon icon={faPlus} />
                         </button>
                      </div>
                   </div>
                </div>
              </div>
            </div>

            {/* Aggregated Totals sidepanel */}
            <div className="tf-grid" style={{ gap: '2rem', position: 'sticky', top: '0' }}>
               <div className="tf-card" style={{ padding: '2rem', border: '1px solid var(--tf-border)' }}>
                  <h3 className="tf-text-accent" style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
                    <FontAwesomeIcon icon={faCalculator} />
                    Final Audit
                  </h3>

                  <div className="tf-grid" style={{ gap: '1.25rem', marginBottom: '2.5rem' }}>
                    <div className="tf-flex" style={{ justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>Gross Subtotal</span>
                      <span style={{ fontWeight: '600' }}>₹{state.invoice.subtotal.toFixed(2)}</span>
                    </div>

                    <div className="tf-grid" style={{ gap: '0.5rem' }}>
                       <div className="tf-flex" style={{ justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                             <FontAwesomeIcon icon={faTag} size="xs" /> Applied Discount (%)
                          </span>
                       </div>
                       <div style={{ position: 'relative' }}>
                          <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--tf-text-muted)', fontWeight: '700' }}>%</span>
                          <input
                             className="tf-input"
                             style={{ paddingRight: '2.4rem' }}
                             type="number"
                             value={state.invoice.discount === 0 ? '' : state.invoice.discount}
                             onChange={handleDiscountChange}
                             placeholder="0.00"
                          />
                       </div>
                    </div>
                    
                    {state.invoice.discount > 0 && (
                      <div className="tf-flex" style={{ justifyContent: 'space-between', marginTop: '-0.5rem' }}>
                        <span style={{ color: 'var(--tf-text-muted)', fontSize: '0.8rem' }}>Discount Amount</span>
                        <span style={{ fontSize: '0.85rem' }}>-₹{state.invoice.discountAmount?.toFixed(2) || '0.00'}</span>
                      </div>
                    )}

                    <div className="tf-flex" style={{ justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>CGST (2.5%)</span>
                      <span>₹{state.invoice.cgst.toFixed(2)}</span>
                    </div>

                    <div className="tf-flex" style={{ justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>SGST (2.5%)</span>
                      <span>₹{state.invoice.sgst.toFixed(2)}</span>
                    </div>

                    <div className="tf-flex" style={{ justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>Adjustment (Round Off)</span>
                      <span>₹{state.invoice.roundOffAmount.toFixed(2)}</span>
                    </div>

                    {(() => {
                      const rti = state.invoice.reservedTableInfo;
                      if (!rti) return false;
                      if (Array.isArray(rti)) return rti.length > 0 && rti.some(info => info.status === 'succeeded' || info.tableNumber);
                      return rti.status === 'succeeded' || rti.tableNumber;
                    })() && (
                      <div className="tf-flex" style={{ justifyContent: 'space-between', color: 'var(--tf-success)', fontWeight: '600' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FontAwesomeIcon icon={faCalculator} size="xs" /> Table Reservation</span>
                        <span>-₹100.00</span>
                      </div>
                    )}
                  </div>

                  <div style={{ borderTop: '1px solid var(--tf-border)', paddingTop: '1.5rem', marginBottom: '2.5rem' }}>
                     <div className="tf-flex" style={{ justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>Net Total</span>
                        <span style={{ fontSize: '1.8rem', fontWeight: '900', color: 'var(--tf-primary)' }}>₹{state.invoice.finalAmount}</span>
                     </div>
                  </div>

                  <button 
                    type="submit" 
                    className="tf-btn tf-btn-primary" 
                    style={{ width: '100%', padding: '1.25rem', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', opacity: (state.invoice.foods.length === 0 || state.invoice.status === 'cancelled') ? 0.5 : 1 }}
                    disabled={state.loading || state.invoice.foods.length === 0 || state.invoice.status === 'cancelled'}
                  >
                    <FontAwesomeIcon icon={faSave} />
                    Commit Modifications
                  </button>
                  {state.invoice.status === 'cancelled' && (
                    <p style={{ color: 'var(--tf-error)', fontSize: '0.75rem', textAlign: 'center', marginTop: '1rem', fontWeight: '600' }}>
                      <FontAwesomeIcon icon={faCircleExclamation} /> THIS INVOICE IS VOIDED AND CANNOT BE ALTERED
                    </p>
                  )}
               </div>
               
               <div className="tf-card" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)' }}>
                  <div className="tf-flex" style={{ gap: '0.75rem', marginBottom: '1rem' }}>
                     <FontAwesomeIcon icon={faReceipt} style={{ opacity: 0.5 }} />
                     <span style={{ fontSize: '0.85rem', color: 'var(--tf-text-muted)', textTransform: 'uppercase', fontWeight: '700' }}>Registry Reference</span>
                  </div>
                  <div style={{ fontSize: '0.9rem' }}>
                     <div className="tf-flex" style={{ justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span>Identifier:</span>
                        <span style={{ color: 'var(--tf-text-white)', fontWeight: '600' }}>#{state.invoice.invoiceNumber}</span>
                     </div>
                     <div className="tf-flex" style={{ justifyContent: 'space-between' }}>
                        <span>Logged Date:</span>
                        <span style={{ color: 'var(--tf-text-white)', fontWeight: '600' }}>{new Date(state.invoice.invoiceDate).toLocaleDateString()}</span>
                     </div>
                  </div>
               </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditInvoice;